import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { UpdateOffer } from "~/hooks/useUpdater";

const RESTART_ERROR = "Couldn't install the update. Reopen Chat to try again.";
const SKIP_ERROR = "Couldn't skip this version. Try again.";

type ActionKind = "restart" | "skip";

export function UpdateChip({ offer }: { offer: UpdateOffer | null }) {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [running, setRunning] = useState<ActionKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (offer === null) {
      setRevealed(false);
      setOpen(false);
      setError(null);
      setRunning(null);
      return;
    }
    const frame = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(frame);
  }, [offer]);

  const close = useCallback(() => {
    setOpen(false);
    chipRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (containerRef.current?.contains(event.target as Node)) return;
      close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const runAction = useCallback(
    async (kind: ActionKind) => {
      if (running !== null || !offer) return;
      setError(null);
      setRunning(kind);
      try {
        await (kind === "restart" ? offer.restart() : offer.skip());
      } catch {
        setError(kind === "restart" ? RESTART_ERROR : SKIP_ERROR);
      } finally {
        setRunning(null);
      }
    },
    [running, offer],
  );

  if (offer === null) return null;

  const name = `Nurion Chat ${offer.version} is ready`;

  const toggle = () => setOpen((current) => !current);

  const chipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--chat-space-2)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "var(--chat-space-1) var(--chat-space-2)",
    borderRadius: "var(--chat-radius-sm)",
    opacity: revealed ? 1 : 0,
    transition: reducedMotion ? undefined : "opacity var(--chat-motion-fast)",
  };

  const dotStyle: CSSProperties = {
    width: 6,
    height: 6,
    flexShrink: 0,
    borderRadius: "var(--chat-radius-full)",
    background: "var(--chat-accent)",
  };

  const labelStyle: CSSProperties = {
    color: "var(--chat-fg-secondary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-xs-size)",
    lineHeight: "var(--chat-type-body-xs-line)",
  };

  const popoverStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + var(--chat-space-2))",
    right: 0,
    zIndex: 60,
    width: 260,
    maxWidth: "calc(100vw - 2 * var(--chat-space-4))",
    padding: "var(--chat-space-3)",
    background: "var(--chat-surface-overlay)",
    border: "1px solid var(--chat-surface-border)",
    borderRadius: "var(--chat-radius-lg)",
    boxShadow: "var(--chat-shadow-popover)",
  };

  const copyStyle: CSSProperties = {
    margin: 0,
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
  };

  const rowStyle: CSSProperties = {
    display: "flex",
    gap: "var(--chat-space-2)",
    marginTop: "var(--chat-space-2)",
  };

  const primaryStyle: CSSProperties = {
    background: "var(--chat-cta-bg)",
    color: "var(--chat-cta-fg)",
    border: "none",
    cursor: running === null ? "pointer" : "default",
    padding: "var(--chat-space-1) var(--chat-space-2)",
    borderRadius: "var(--chat-radius-sm)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
    opacity: running !== null ? 0.5 : 1,
  };

  const secondaryStyle: CSSProperties = {
    background: "transparent",
    color: "var(--chat-fg-secondary)",
    border: "none",
    cursor: running === null ? "pointer" : "default",
    padding: "var(--chat-space-1) var(--chat-space-2)",
    borderRadius: "var(--chat-radius-sm)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
    opacity: running !== null ? 0.5 : 1,
  };

  const errorStyle: CSSProperties = {
    margin: "var(--chat-space-2) 0 0",
    color: "var(--chat-state-danger)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
  };

  return (
    <div ref={containerRef} style={{ position: "relative", marginLeft: "auto" }}>
      <button
        ref={chipRef}
        type="button"
        className="chat-button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={name}
        title={name}
        style={chipStyle}
      >
        <span aria-hidden style={dotStyle} />
        <span style={labelStyle}>Update</span>
      </button>

      {open && (
        <div role="dialog" aria-label="Update" style={popoverStyle}>
          <p style={copyStyle}>{`Nurion Chat ${offer.version} is ready.`}</p>
          <div style={rowStyle}>
            <button
              type="button"
              className="chat-button"
              onClick={() => void runAction("restart")}
              disabled={running !== null}
              style={primaryStyle}
            >
              {running === "restart" ? "Restarting…" : "Restart to update"}
            </button>
            <button
              type="button"
              className="chat-button"
              onClick={() => void runAction("skip")}
              disabled={running !== null}
              style={secondaryStyle}
            >
              Skip this version
            </button>
          </div>
          {error && <p style={errorStyle}>{error}</p>}
        </div>
      )}
    </div>
  );
}
